import { useState, useEffect, useCallback } from "react";
import { jsonOrThrow } from "./apiError";
import { PairingCodeModal } from "./ProvisioningPage";
import { LIVENESS, DISPLAY_POWER_NOTE, flattenFleet, countByState, fmtTime } from "./deviceStatusViewModel";

// ============================================================================
// 設備管理 — device status + add device
// ============================================================================
// Status: GET /api/v1/provisioning/fleet (tenant-scoped by the bearer token;
// liveness computed server-side). A failed load shows an error state, never
// empty zeros.
// Add device: the operator picks one of their venues, then the existing
// venue-scoped PairingCodeModal mints a code via
// POST /api/v1/provisioning/pairing-codes { venue_id, count: 1 }. The TV enters
// that code; nothing is entered here.
// ============================================================================

const API = "https://api.briolabs.io";
export const FLEET_PATH = "/api/v1/provisioning/fleet";

export async function loadFleet() {
  const token = localStorage.getItem("biz_token");
  const res = await fetch(`${API}${FLEET_PATH}`, {
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  const json = await jsonOrThrow(res, FLEET_PATH);
  if (!json || json.success !== true || !Array.isArray(json.data?.venues) || !Array.isArray(json.data?.unbound_devices)) {
    throw new Error("INVALID_FLEET_RESPONSE");
  }
  return flattenFleet(json.data);
}

const t = {
  bg: "#0F1117", card: "#1A1D27", border: "#2A2E3A",
  accent: "#6C5CE7", accentLight: "#A29BFE",
  success: "#00B894", warning: "#FDCB6E", danger: "#E17055",
  text: "#E8E8ED", textDim: "#8B8FA3", dim: "#5A5E72",
};
const toneColor = { success: t.success, warning: t.warning, danger: t.danger, dim: t.dim };
const card = { background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, padding: 20 };
const btn = { padding: "10px 18px", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", border: "1px solid transparent" };
const btnPrimary = { ...btn, background: t.accent, color: "#fff", borderColor: t.accent };
const btnSecondary = { ...btn, background: "transparent", color: t.text, borderColor: t.border };

function describeLoadError(err) {
  if (err && err.status === 401) return "登入已過期，請重新登入後再試。";
  if (err && err.status) return `伺服器回應錯誤（HTTP ${err.status}），請稍後重試。`;
  return "無法讀取設備狀態（網路或回應格式錯誤），請稍後重試。";
}

function StatusBadge({ state }) {
  const info = state === "disabled" ? { label: "已停用", tone: "dim" } : LIVENESS[state] || LIVENESS.unavailable;
  return (
    <span data-testid="device-status" data-state={state} style={{ display: "inline-flex", alignItems: "center", gap: 6, ...(state === "disabled" ? { color: t.textDim } : {}) }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: toneColor[info.tone] }} />
      {info.label}
    </span>
  );
}

function VenuePicker({ venues, onCancel, onConfirm }) {
  const [venueId, setVenueId] = useState("");
  return (
    <div data-testid="venue-picker" style={{ ...card, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>選擇要新增設備的場域</div>
      {venues.length === 0 ? (
        <div style={{ fontSize: 13, color: t.textDim }}>尚無場域，請先至「場域管理」建立場域。</div>
      ) : (
        <select
          aria-label="場域"
          value={venueId}
          onChange={(e) => setVenueId(e.target.value)}
          style={{ width: "100%", padding: "10px 12px", background: t.bg, border: `1px solid ${t.border}`, borderRadius: 8, color: t.text, fontSize: 13 }}
        >
          <option value="">請選擇場域</option>
          {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
        <button onClick={onCancel} style={btnSecondary}>取消</button>
        <button
          onClick={() => onConfirm(venues.find((v) => v.id === venueId))}
          style={{ ...btnPrimary, opacity: venueId ? 1 : 0.5 }}
          disabled={!venueId}
        >
          下一步：產生配對碼
        </button>
      </div>
    </div>
  );
}

export default function DevicesPage() {
  const [state, setState] = useState({ status: "loading" });
  const [picking, setPicking] = useState(false);
  const [pairVenue, setPairVenue] = useState(null);
  const [notice, setNotice] = useState(null);
  const [showDisabled, setShowDisabled] = useState(false);

  const reload = useCallback(() => {
    setState({ status: "loading" });
    loadFleet()
      .then((fleet) => setState({ status: "ready", ...fleet }))
      .catch((error) => setState({ status: "error", error }));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const ready = state.status === "ready";
  const counts = ready ? countByState(state.devices) : null;
  const visibleDevices = ready ? [
    ...state.devices.filter((d) => d.lifecycle === "active"),
    ...state.devices.filter((d) => d.lifecycle === "unknown"),
    ...(showDisabled ? state.devices.filter((d) => d.lifecycle === "disabled") : []),
  ] : [];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>設備管理</h1>
        <button
          data-testid="add-device"
          onClick={() => { setNotice(null); setPicking(true); }}
          style={{ ...btnPrimary, opacity: ready ? 1 : 0.5 }}
          disabled={!ready}
        >
          ➕ 新增設備
        </button>
      </div>

      {notice && (
        <div role="status" style={{ ...card, padding: 12, marginBottom: 16, color: notice.kind === "error" ? t.danger : t.success, fontSize: 13 }}>
          {notice.msg}
        </div>
      )}

      {picking && ready && (
        <VenuePicker
          venues={state.venues}
          onCancel={() => setPicking(false)}
          onConfirm={(venue) => { if (venue) { setPicking(false); setPairVenue(venue); } }}
        />
      )}

      {state.status === "loading" && <div style={{ ...card, color: t.textDim }}>載入中…</div>}

      {state.status === "error" && (
        <div role="alert" data-testid="devices-error" style={{ ...card, borderColor: t.danger }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: t.danger, marginBottom: 6 }}>無法載入設備狀態</div>
          <div style={{ fontSize: 13, color: t.textDim, marginBottom: 12 }}>{describeLoadError(state.error)}</div>
          <button onClick={reload} style={btnSecondary}>重試</button>
        </div>
      )}

      {ready && (
        <>
          <div data-testid="device-counts" style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 13, color: t.textDim, marginBottom: 12 }}>
            <span>總設備 {counts.total}</span>
            <span>在線 {counts.online}</span>
            <span>連線延遲 {counts.delayed}</span>
            <span>離線 {counts.offline}</span>
            <span>從未連線 {counts.never_connected}</span>
            <span>連線無法判定 {counts.unavailable}</span>
            <span>已停用 {counts.disabled}</span>
            <span>生命週期無法判定 {counts.unknown_lifecycle}</span>
          </div>
          <div data-testid="display-power-note" style={{ fontSize: 12, color: t.textDim, marginBottom: 12 }}>{DISPLAY_POWER_NOTE}</div>
          {counts.disabled > 0 && (
            <label style={{ display: "block", color: t.textDim, fontSize: 13, marginBottom: 12 }}>
              <input type="checkbox" checked={showDisabled} onChange={(e) => setShowDisabled(e.target.checked)} />
              顯示已停用設備（{counts.disabled}）
            </label>
          )}
          <div style={card}>
            {visibleDevices.length === 0 ? (
              <div style={{ padding: "32px 12px", textAlign: "center", color: t.textDim, fontSize: 14 }}>
                {state.devices.length === 0
                  ? "尚無設備。按「新增設備」選擇場域並產生配對碼，再於電視上的 brioCAST 輸入該配對碼。"
                  : "目前沒有啟用中的設備。可勾選顯示已停用設備。"}
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                      {["設備名稱", "場域", "狀態", "最後上線", "最後同步", "App 版本"].map((h) => (
                        <th key={h} style={{ textAlign: "left", padding: "10px 12px", color: t.textDim, fontSize: 12, fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleDevices.map((d) => (
                      <tr key={d.id} data-testid="device-row" style={{ borderBottom: `1px solid ${t.border}55` }}>
                        <td style={{ padding: "10px 12px", fontWeight: 600 }}>{d.name}</td>
                        <td style={{ padding: "10px 12px", color: t.textDim }}>{d.venueName || "—"}</td>
                        <td style={{ padding: "10px 12px" }}><StatusBadge state={d.state} /></td>
                        <td style={{ padding: "10px 12px", color: t.textDim }}>{fmtTime(d.lastSeenAt)}</td>
                        <td style={{ padding: "10px 12px", color: t.textDim }}>{fmtTime(d.lastSyncAt)}</td>
                        <td style={{ padding: "10px 12px", color: t.textDim, fontFamily: "monospace" }}>{d.appVersion || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {pairVenue && (
        <PairingCodeModal
          venue={pairVenue}
          zones={pairVenue.zones || []}
          initialZoneId=""
          onClose={() => { setPairVenue(null); reload(); }}
          onToast={setNotice}
          reportError={(e) => setNotice({ kind: "error", msg: e && e.status === 401 ? "登入已過期，請重新登入。" : "產生配對碼失敗，請稍後重試。" })}
        />
      )}
    </div>
  );
}
